/* RequestRevisionPage — full-page side-by-side revision authoring screen.
 *
 * Route: /doctor/cases/:caseId/request-revision
 *
 * The doctor lands here from the Case Detail "Request a Revision" button.
 * Each section mirrors the 6 sections in the Treatment Plan tab:
 *   1. Plan Summary
 *   2. Bite Metrics
 *   3. Clinical Protocol
 *   4. Per-Tooth Plan — Authored Movements
 *   5. Aligners & Wear Schedule
 *   6. Retention Plan
 * On every section the LEFT column shows the planner's plan read-only
 * (mirrors the Treatment Plan tab GlanceBox / KVField / Protocol style)
 * and the RIGHT column has matching editable inputs. Every filled
 * right-side input highlights its row in amber so the diff is visually
 * unambiguous.
 *
 * Submission posts to `PUT /api/cases/{id}/request-revision`. Numeric
 * / chip fields go into `structured_changes`; free-text per-section
 * revisions go into `items[]` with a stable `category` key.
 *
 * iter98.99bf — Restructured the page (2026-02-19) so its sections match
 * the Treatment Plan tab one-to-one. Replaced the bespoke green/amber
 * `SplitSection` chrome with the same ApprovalSection-style header band
 * (3px primary bar + icon + title in secondary/60) so the flow looks
 * cohesive end-to-end. Added Per-Tooth (4 categories) + Aligners-and-
 * Wear-Schedule sections that previously didn't exist; merged the
 * standalone "Plan Viewer" link into the page header strip; relocated
 * "Overall notes" into the page header strip (it's a one-line summary,
 * not a section).
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
    CaretLeft, Clock, ClipboardText, NotePencil, CheckCircle, ArrowRight,
    Info, Circle,
    ListChecks, ChartLine, Compass, Pill, Anchor,
    ArrowsHorizontal,
} from "@phosphor-icons/react";
import CaseSubmittedDialog from "@/components/case/CaseSubmittedDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { confirmDialog } from "@/lib/confirm";
import { PerToothCategoryView, PerToothChartOnly } from "@/components/case/PlanApprovalHero";


/* All field keys the right column tracks. Stable keys → backend stores
   them on `structured_changes` (numeric/chip) or in `items[]` (text).
   iter98.99bg — `retainer_type` removed: doctor cannot request a
   change to the retainer type from this surface (only recall and wear
   protocol). */
const STRUCT_KEYS = [
    "aligner_count_upper", "aligner_count_lower",
    "duration_weeks", "wear_days",
    "overjet_initial_mm", "overbite_initial_mm",
    "final_overjet_mm", "final_overbite_mm", "total_ipr_mm",
    "follow_up_recall_months", "wear_protocol_text",
];

const STRUCT_LABELS = {
    aligner_count_upper:    { label: "Upper aligners",      unit: ""    },
    aligner_count_lower:    { label: "Lower aligners",      unit: ""    },
    duration_weeks:         { label: "Duration",            unit: "w"   },
    wear_days:              { label: "Wear schedule",       unit: "d"   },
    overjet_initial_mm:     { label: "Initial overjet",     unit: "mm"  },
    overbite_initial_mm:    { label: "Initial overbite",    unit: "mm"  },
    final_overjet_mm:       { label: "Final overjet",       unit: "mm"  },
    final_overbite_mm:      { label: "Final overbite",      unit: "mm"  },
    total_ipr_mm:           { label: "Total IPR",           unit: "mm"  },
    follow_up_recall_months:{ label: "Follow-up recall",    unit: "mo"  },
    wear_protocol_text:     { label: "Wear protocol",       unit: ""    },
};

/* iter98.99bf — extended TEXT_ITEMS to cover Per-Tooth (4 categories) and
   Aligners-and-Wear-Schedule (notes), so every Treatment Plan section
   maps cleanly onto a revision text field. */
const TEXT_ITEMS = [
    { key: "attachments_revision",          label: "Attachments revision"          },
    { key: "ipr_revision",                  label: "IPR revision"                  },
    { key: "elastics_revision",             label: "Elastics revision"             },
    { key: "rationale_revision",            label: "Rationale revision"            },
    { key: "overcorrection_revision",       label: "Overcorrection revision"       },
    { key: "per_tooth_movements_revision",  label: "Per-tooth movements revision"  },
    { key: "per_tooth_attachments_revision",label: "Per-tooth attachments revision"},
    { key: "per_tooth_cuts_revision",       label: "Per-tooth precision cuts revision"},
    { key: "per_tooth_biteramps_revision",  label: "Per-tooth bite ramps revision" },
    { key: "wear_schedule_notes",           label: "Wear schedule notes"           },
    { key: "overall_notes",                 label: "Overall notes"                 },
];

/* iter98.99bg — Retainer chip array dropped: doctor can no longer
   request a retainer-type change. Kept only the label lookup so we
   can humanise the planner's retainer_type on the read-only side. */
const RETAINER_LABEL = {
    vivera:       "Clear (Vivera-style)",
    hawley:       "Hawley",
    fixed_bonded: "Fixed bonded",
    dual:         "Dual (clear + fixed)",
    other:        "Other",
};


function fmtShortDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString("en-IN", {
            day: "numeric", month: "short", year: "numeric",
        });
    } catch { return ""; }
}

const humanize = (s) => (s ? String(s).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "");


export default function RequestRevisionPage() {
    const { caseId } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [caseData, setCaseData] = useState(null);

    const [struct, setStruct] = useState({});
    const [textRevs, setTextRevs] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);
    const [sentAt, setSentAt] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [reviewOpen, setReviewOpen] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/cases/${caseId}`);
                if (!cancelled) {
                    setCaseData(data);
                    const reqs = data?.revision_requests || [];
                    const openReq = [...reqs].reverse().find((r) => r?.status === "open");
                    if (openReq) {
                        setSent(true);
                        setSentAt(openReq.requested_at || null);
                    }
                }
            } catch (err) {
                if (!cancelled) {
                    toast.error(err.response?.data?.detail || "Failed to load case");
                    navigate(-1);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [caseId, navigate]);

    const plan = caseData?.plan || {};
    const planSubmittedAt = caseData?.plan_submitted_at || caseData?.timeline?.find?.(
        (t) => t.action === "sent_for_approval" || t.action === "plan_ready_for_review"
    )?.created_at;

    // ── Change counting ───────────────────────────────────────────────
    const structFilled = useMemo(
        () => STRUCT_KEYS.filter((k) => struct[k] !== undefined && struct[k] !== null && struct[k] !== ""),
        [struct]
    );
    const textFilled = useMemo(
        () => TEXT_ITEMS.map((t) => t.key).filter((k) => (textRevs[k] || "").trim()),
        [textRevs]
    );
    const totalChanges = structFilled.length + textFilled.length;

    const diffRows = useMemo(() => {
        const rows = [];
        STRUCT_KEYS.forEach((k) => {
            const v = struct[k];
            if (v === undefined || v === null || v === "") return;
            const meta = STRUCT_LABELS[k] || { label: k, unit: "" };
            let oldVal = plan[k];
            if (oldVal === undefined || oldVal === null || oldVal === "") {
                oldVal = caseData?.clinical?.[k];
            }
            const fmt = (val) => {
                if (val === undefined || val === null || val === "") return "—";
                if (k === "wear_protocol_text") {
                    const s = String(val);
                    return s.length > 38 ? `${s.slice(0, 38)}…` : s;
                }
                return `${val}${meta.unit}`;
            };
            rows.push({ key: `s-${k}`, kind: "struct", label: meta.label, from: fmt(oldVal), to: fmt(v) });
        });
        TEXT_ITEMS.forEach(({ key, label }) => {
            const txt = (textRevs[key] || "").trim();
            if (!txt) return;
            const preview = txt.length > 64 ? `${txt.slice(0, 64)}…` : txt;
            rows.push({ key: `t-${key}`, kind: "text", label, preview });
        });
        return rows;
    }, [struct, textRevs, plan, caseData?.clinical]);

    // ── Field handlers ────────────────────────────────────────────────
    const setStructValue = (k, v) => setStruct((p) => ({ ...p, [k]: v }));
    const setTextValue = (k, v) => setTextRevs((p) => ({ ...p, [k]: v }));

    // ── Submit ────────────────────────────────────────────────────────
    const handleSend = async () => {
        if (totalChanges === 0) return;
        setSubmitting(true);
        try {
            const items = TEXT_ITEMS
                .filter(({ key }) => (textRevs[key] || "").trim())
                .map(({ key }) => ({
                    category: key,
                    teeth: [],
                    note: textRevs[key].trim(),
                    severity: "normal",
                }));
            const structured_changes = {};
            STRUCT_KEYS.forEach((k) => {
                if (struct[k] !== undefined && struct[k] !== null && struct[k] !== "") {
                    structured_changes[k] = struct[k];
                }
            });
            const summary = (textRevs.overall_notes || "").trim()
                || `Revision request: ${totalChanges} change${totalChanges === 1 ? "" : "s"} across ${[
                    structFilled.length ? "plan values" : null,
                    textFilled.length ? "protocol notes" : null,
                ].filter(Boolean).join(" and ")}.`;

            await api.put(`/cases/${caseId}/request-revision`, {
                note: summary,
                items,
                structured_changes,
            });
            const nowIso = new Date().toISOString();
            setSent(true);
            setSentAt(nowIso);
            setConfirmOpen(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to send revision");
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = async () => {
        if (totalChanges > 0 && !sent) {
            if (!await confirmDialog({ title: "You have unsaved changes. Cancel anyway?", confirmText: "Confirm", tone: "default" })) return;
        }
        navigate(`/doctor/cases/${caseId}`);
    };

    if (loading) {
        return <div className="p-8 text-center text-sm text-muted-foreground">Loading case…</div>;
    }
    if (!caseData) return null;

    const caseCode = caseData.code || `#${(caseData.id || "").slice(0, 8)}`;
    const patientName = caseData.patient_name || "Patient";

    return (
        <div className="min-h-screen bg-[#f7f8fa] pb-24" data-testid="request-revision-page">
            {/* ── TOP BAR ─────────────────────────────────────── */}
            <div className="bg-white border-b border-border px-6 sm:px-8 py-4 flex items-start justify-between gap-4">
                <div className="flex flex-col gap-0.5">
                    <button
                        onClick={() => navigate(`/doctor/cases/${caseId}`)}
                        className="text-xs text-primary hover:underline flex items-center gap-1 w-fit mb-1"
                        data-testid="back-to-plan-review"
                    >
                        <CaretLeft size={14} weight="bold" /> Back to Plan Review
                    </button>
                    <div className="text-xl font-medium leading-tight">Request a Revision</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                        Case {caseCode} · {patientName}
                    </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                    <Badge tone="amber" data-testid="revision-status-badge">
                        <Clock size={11} weight="duotone" /> Pending Approval
                    </Badge>
                    <p className="text-[11px] text-muted-foreground/80 text-right max-w-[260px] leading-snug">
                        Fill only the fields you want changed.<br />
                        Leave blank = keep the planner&apos;s value.
                    </p>
                </div>
            </div>

            <div className="max-w-[1280px] mx-auto px-6 sm:px-8 pt-6">

                {/* Lock-state banner */}
                {sent && (
                    <div
                        className="flex items-start gap-2.5 bg-emerald-50 border border-primary/60 rounded-lg px-4 py-3 mb-5 text-[13px] text-emerald-900"
                        data-testid="revision-success-banner"
                    >
                        <CheckCircle size={16} weight="fill" className="shrink-0 mt-0.5 text-primary" />
                        <div className="leading-snug">
                            <div className="font-semibold">
                                Revision request sent{sentAt ? ` on ${fmtShortDate(sentAt)}` : ""}.
                            </div>
                            <div className="text-emerald-900/80 mt-0.5">
                                This page is locked until the planner actions your changes — you&apos;ll be notified the moment the revised plan is back in your queue.
                            </div>
                        </div>
                    </div>
                )}

                {/* iter98.99bh — Overall notes moved to the BOTTOM of
                    the page (was up top in 98.99bg). The doctor fills
                    out per-section revisions first, then writes a
                    one-line summary at the end — natural flow. */}

                {/* Column headers */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-0 mb-4">
                    <ColumnHeader
                        icon={<ClipboardText size={14} className="text-primary" weight="duotone" />}
                        title="Planner's Plan"
                        sub={planSubmittedAt ? `Submitted ${fmtShortDate(planSubmittedAt)}` : "Submitted plan"}
                        rightBadge={<Badge tone="gray">Read-only</Badge>}
                    />
                    <ColumnHeader
                        icon={<NotePencil size={14} className="text-amber-600" weight="duotone" />}
                        title="Your Revision Request"
                        sub="Leave blank to keep planner's value"
                        rightBadge={
                            <Badge tone={totalChanges > 0 ? "amber" : "gray"} data-testid="change-count-badge">
                                {totalChanges} change{totalChanges === 1 ? "" : "s"}
                            </Badge>
                        }
                    />
                </div>

                {/* ══════════════════════════════════════════════════════
                    The 6 sections (mirror the Treatment Plan tab)
                ═══════════════════════════════════════════════════════ */}

                {/* 1 ▸ PLAN SUMMARY */}
                <RevisionSection title="Plan Summary" icon={ListChecks} testId="plan-summary">
                    <PlanSummaryLeft plan={plan} struct={struct} />
                    <PlanSummaryRight struct={struct} setStructValue={setStructValue} sent={sent} />
                </RevisionSection>

                {/* 2 ▸ BITE METRICS */}
                <RevisionSection title="Bite Metrics" icon={ChartLine} testId="bite-metrics">
                    <BiteMetricsLeft plan={plan} struct={struct} />
                    <BiteMetricsRight struct={struct} setStructValue={setStructValue} sent={sent} />
                </RevisionSection>

                {/* 3 ▸ CLINICAL PROTOCOL */}
                <RevisionSection title="Clinical Protocol" icon={ClipboardText} testId="clinical-protocol">
                    <ClinicalProtocolLeft plan={plan} textRevs={textRevs} />
                    <ClinicalProtocolRight textRevs={textRevs} setTextValue={setTextValue} sent={sent} />
                </RevisionSection>

                {/* 4 ▸ PER-TOOTH PLAN — TOOTH MOVEMENT
                    iter98.99bi → 98.99bj — Each category has its own
                    section. Both columns now mirror each other: LEFT =
                    planner's compact chart + authored summary, RIGHT =
                    same chart (mirrored, smaller) + revision text. */}
                <RevisionSection title="Per-tooth Plan — Tooth Movement" icon={Compass} testId="per-tooth-movements">
                    <PerToothCategoryView plan={plan} category="movements" compact />
                    <PerToothRevisionRight
                        plan={plan}
                        category="movements"
                        label="Movements revision"
                        placeholder="e.g. UR3 rotation is too aggressive — soften to 7° over stages 4–8; LL5 distalisation needs +1mm…"
                        value={textRevs.per_tooth_movements_revision || ""}
                        onChange={(v) => setTextValue("per_tooth_movements_revision", v)}
                        disabled={sent}
                        testid="revision-input-per-tooth-movements"
                    />
                </RevisionSection>

                {/* 5 ▸ PER-TOOTH PLAN — ATTACHMENTS */}
                <RevisionSection title="Per-tooth Plan — Attachments" icon={Compass} testId="per-tooth-attachments">
                    <PerToothCategoryView plan={plan} category="attachments" compact />
                    <PerToothRevisionRight
                        plan={plan}
                        category="attachments"
                        label="Attachments revision"
                        placeholder="e.g. Move the bevel attachment on UR2 to stage 6; add gingivally-loaded rectangle to LR4…"
                        value={textRevs.per_tooth_attachments_revision || ""}
                        onChange={(v) => setTextValue("per_tooth_attachments_revision", v)}
                        disabled={sent}
                        testid="revision-input-per-tooth-attachments"
                    />
                </RevisionSection>

                {/* 6 ▸ PER-TOOTH PLAN — PRECISION CUTS */}
                <RevisionSection title="Per-tooth Plan — Precision Cuts" icon={Compass} testId="per-tooth-cuts">
                    <PerToothCategoryView plan={plan} category="cuts" compact />
                    <PerToothRevisionRight
                        plan={plan}
                        category="cuts"
                        label="Precision cuts revision"
                        placeholder="e.g. Add precision cuts on UL3 and LR3 for class-II elastics starting stage 8…"
                        value={textRevs.per_tooth_cuts_revision || ""}
                        onChange={(v) => setTextValue("per_tooth_cuts_revision", v)}
                        disabled={sent}
                        testid="revision-input-per-tooth-cuts"
                    />
                </RevisionSection>

                {/* 7 ▸ PER-TOOTH PLAN — BITE RAMPS */}
                <RevisionSection title="Per-tooth Plan — Bite Ramps" icon={Compass} testId="per-tooth-biteramps">
                    <PerToothCategoryView plan={plan} category="biteramps" compact />
                    <PerToothRevisionRight
                        plan={plan}
                        category="biteramps"
                        label="Bite ramps revision"
                        placeholder="e.g. Remove bite ramps from UR2/UL2 — patient cannot tolerate; consider posterior only…"
                        value={textRevs.per_tooth_biteramps_revision || ""}
                        onChange={(v) => setTextValue("per_tooth_biteramps_revision", v)}
                        disabled={sent}
                        testid="revision-input-per-tooth-biteramps"
                    />
                </RevisionSection>

                {/* 5 ▸ ALIGNERS & WEAR SCHEDULE */}
                <RevisionSection title="Aligners & Wear Schedule" icon={Pill} testId="wear-schedule">
                    <WearScheduleLeft plan={plan} struct={struct} textRevs={textRevs} />
                    <WearScheduleRight
                        struct={struct} setStructValue={setStructValue}
                        textRevs={textRevs} setTextValue={setTextValue}
                        sent={sent}
                    />
                </RevisionSection>

                {/* 6 ▸ RETENTION PLAN */}
                <RevisionSection title="Retention Plan" icon={Anchor} testId="retention-plan">
                    <RetentionLeft plan={plan} struct={struct} />
                    <RetentionRight struct={struct} setStructValue={setStructValue} sent={sent} />
                </RevisionSection>

                {/* iter98.99bh — Overall notes sits at the bottom now,
                    after the per-section revisions. It's the doctor's
                    summary read for the planner. */}
                <ContextStrip
                    overallNotes={textRevs.overall_notes || ""}
                    onOverallChange={(v) => setTextValue("overall_notes", v)}
                    disabled={sent}
                />

            </div>

            {/* ── STICKY BOTTOM BAR ── */}
            <div className="fixed bottom-0 inset-x-0 bg-white border-t border-border px-6 sm:px-8 py-3 flex items-center justify-between z-50 shadow-[0_-2px_12px_rgba(0,0,0,0.06)]" data-testid="revision-sticky-bar">
                <div
                    className={`flex items-center gap-1.5 text-[13px] font-medium ${
                        totalChanges > 0 ? "text-amber-800" : "text-muted-foreground"
                    }`}
                >
                    <Info size={14} weight={totalChanges > 0 ? "fill" : "duotone"} />
                    <span data-testid="revision-change-pill">
                        {sent
                            ? `Revision Requested — ${totalChanges} change${totalChanges === 1 ? "" : "s"} sent`
                            : totalChanges === 0
                                ? "No changes yet"
                                : `${totalChanges} change${totalChanges === 1 ? "" : "s"} requested`}
                    </span>
                </div>
                <div className="flex items-center gap-2.5">
                    {sent ? (
                        <Button
                            variant="outline"
                            onClick={() => navigate(`/doctor/cases/${caseId}`)}
                            className="border-primary text-primary hover:bg-primary/10"
                            data-testid="back-to-case-overview"
                        >
                            Back to Case Overview
                        </Button>
                    ) : (
                        <>
                            <Button
                                variant="ghost"
                                onClick={handleCancel}
                                className="border border-border text-muted-foreground"
                                data-testid="revision-cancel-btn"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={() => setReviewOpen(true)}
                                disabled={totalChanges === 0 || submitting}
                                className="bg-primary hover:bg-primary/90 text-white disabled:bg-border disabled:text-muted-foreground"
                                data-testid="revision-send-btn"
                            >
                                Request Revision <ArrowRight size={14} weight="bold" className="ml-1.5" />
                            </Button>
                        </>
                    )}
                </div>
            </div>

            <CaseSubmittedDialog
                open={confirmOpen}
                flow="doctor_revision_to_planner"
                caseCode={caseData?.code || ""}
                primaryLabel="Back to case"
                secondaryLabel="Stay here"
                onPrimary={() => {
                    setConfirmOpen(false);
                    navigate(`/doctor/cases/${caseId}`);
                }}
                onSecondary={() => setConfirmOpen(false)}
            />

            <ReviewChangesDialog
                open={reviewOpen}
                onClose={() => setReviewOpen(false)}
                rows={diffRows}
                submitting={submitting}
                onConfirm={async () => {
                    await handleSend();
                    setReviewOpen(false);
                }}
            />
        </div>
    );
}


/* ══════════════════════════════════════════════════════════════════
   Section wrapper (matches PlanApprovalHero's ApprovalSection chrome).
   `layout`:
     - "split" (default) → 2 columns inside the card (planner|doctor)
     - "stacked"         → children render full-width, vertically stacked
       (used by Per-Tooth, where the 4-tab FDI UI doesn't fit a column)
═══════════════════════════════════════════════════════════════════ */

function RevisionSection({ title, icon: Icon, children, testId, layout = "split" }) {
    return (
        <div
            className="rounded-2xl border border-border bg-card overflow-hidden mb-5 shadow-sm"
            data-testid={`revision-section-${testId}`}
        >
            <div className="bg-secondary/60 px-4 py-3 border-b border-border/70 flex items-center gap-3">
                <span className="shrink-0 w-[3px] h-5 rounded-sm bg-primary" />
                {Icon && <Icon size={15} weight="duotone" className="text-primary shrink-0" />}
                <span className="font-display text-sm font-semibold text-foreground">{title}</span>
            </div>
            {layout === "stacked" ? (
                <div>
                    {React.Children.map(children, (child, i) => (
                        <div
                            className={`p-4 sm:p-5 ${i === 0 ? "bg-secondary/30 border-b border-border/70" : "bg-card"}`}
                            data-side={i === 0 ? "planner" : "doctor"}
                        >
                            <div className="flex items-center gap-1.5 mb-3 text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">
                                <span className={`w-1.5 h-1.5 rounded-full ${i === 0 ? "bg-primary" : "bg-amber-500"}`} />
                                {i === 0 ? "Planner's plan" : "Your revision"}
                            </div>
                            {child}
                        </div>
                    ))}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 md:divide-x divide-border">
                    {React.Children.map(children, (child, i) => (
                        <div
                            className={`p-4 sm:p-5 ${i === 0 ? "bg-secondary/30" : "bg-card"}`}
                            data-side={i === 0 ? "planner" : "doctor"}
                        >
                            <div className="flex items-center gap-1.5 mb-3 text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">
                                <span className={`w-1.5 h-1.5 rounded-full ${i === 0 ? "bg-primary" : "bg-amber-500"}`} />
                                {i === 0 ? "Planner's plan" : "Your revision"}
                            </div>
                            {child}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}


/* ══════════════════════════════════════════════════════════════════
   1 ▸ Plan Summary
═══════════════════════════════════════════════════════════════════ */

function PlanSummaryLeft({ plan, struct }) {
    const upper = plan.aligner_count_upper;
    const lower = plan.aligner_count_lower;
    const cadence = plan.change_interval_days || plan.days_per_aligner;
    const weeks = plan.estimated_duration_weeks;
    return (
        <GlanceBox>
            <GlanceRow
                k="Aligners (Upper / Lower)"
                v={upper != null || lower != null ? `${upper ?? "—"} U / ${lower ?? "—"} L` : null}
                changed={!!(struct.aligner_count_upper || struct.aligner_count_lower)}
            />
            <GlanceRow
                k="Wear schedule"
                v={cadence ? `${cadence} days per stage` : null}
                changed={!!struct.wear_days}
            />
            <GlanceRow
                k="Estimated duration"
                v={weeks ? `${weeks} weeks (${weeks * 7} days)` : null}
                changed={!!struct.duration_weeks}
            />
            <GlanceRow
                k="Est. completion"
                v={plan.estimated_completion_date}
            />
        </GlanceBox>
    );
}

function PlanSummaryRight({ struct, setStructValue, sent }) {
    return (
        <GlanceBox>
            <NumInputRow
                label="Upper aligners" unit="aligners" placeholder="e.g. 16"
                value={struct.aligner_count_upper} onChange={(v) => setStructValue("aligner_count_upper", v)}
                min={0} max={60} disabled={sent}
                testid="revision-input-aligner-upper"
            />
            <NumInputRow
                label="Lower aligners" unit="aligners" placeholder="e.g. 16"
                value={struct.aligner_count_lower} onChange={(v) => setStructValue("aligner_count_lower", v)}
                min={0} max={60} disabled={sent}
                testid="revision-input-aligner-lower"
            />
            <NumInputRow
                label="Duration" unit="weeks" placeholder="e.g. 30"
                value={struct.duration_weeks} onChange={(v) => setStructValue("duration_weeks", v)}
                min={1} disabled={sent}
                testid="revision-input-duration-weeks"
            />
        </GlanceBox>
    );
}


/* ══════════════════════════════════════════════════════════════════
   2 ▸ Bite Metrics
═══════════════════════════════════════════════════════════════════ */

function BiteMetricsLeft({ plan, struct }) {
    return (
        <GlanceBox>
            <GlanceRow
                k="Initial overjet"
                v={plan.initial_overjet_mm != null ? `${plan.initial_overjet_mm} mm` : null}
                changed={!!struct.overjet_initial_mm}
            />
            <GlanceRow
                k="Initial overbite"
                v={plan.initial_overbite_mm != null ? `${plan.initial_overbite_mm} mm` : null}
                changed={!!struct.overbite_initial_mm}
            />
            <GlanceRow
                k="Final overjet"
                v={plan.final_overjet_mm != null ? `${plan.final_overjet_mm} mm` : null}
                changed={!!struct.final_overjet_mm}
            />
            <GlanceRow
                k="Final overbite"
                v={plan.final_overbite_mm != null ? `${plan.final_overbite_mm} mm` : null}
                changed={!!struct.final_overbite_mm}
            />
            <GlanceRow
                k="Total IPR"
                v={plan.total_ipr_mm != null ? <span className="inline-flex items-center gap-1 text-amber-700"><ArrowsHorizontal size={11} weight="fill" /> {plan.total_ipr_mm} mm</span> : null}
                changed={!!struct.total_ipr_mm}
            />
        </GlanceBox>
    );
}

function BiteMetricsRight({ struct, setStructValue, sent }) {
    return (
        <GlanceBox>
            <NumInputRow label="Initial overjet" unit="mm" placeholder="e.g. 4.0" step="0.1"
                value={struct.overjet_initial_mm} onChange={(v) => setStructValue("overjet_initial_mm", v)}
                disabled={sent} testid="revision-input-overjet-initial" />
            <NumInputRow label="Initial overbite" unit="mm" placeholder="e.g. 2.5" step="0.1"
                value={struct.overbite_initial_mm} onChange={(v) => setStructValue("overbite_initial_mm", v)}
                disabled={sent} testid="revision-input-overbite-initial" />
            <NumInputRow label="Final overjet" unit="mm" placeholder="e.g. 1.5" step="0.1"
                value={struct.final_overjet_mm} onChange={(v) => setStructValue("final_overjet_mm", v)}
                disabled={sent} testid="revision-input-final-overjet" />
            <NumInputRow label="Final overbite" unit="mm" placeholder="e.g. 1.0" step="0.1"
                value={struct.final_overbite_mm} onChange={(v) => setStructValue("final_overbite_mm", v)}
                disabled={sent} testid="revision-input-final-overbite" />
            <NumInputRow label="Total IPR" unit="mm" placeholder="e.g. 2.0" step="0.1"
                value={struct.total_ipr_mm} onChange={(v) => setStructValue("total_ipr_mm", v)}
                disabled={sent} testid="revision-input-total-ipr" />
        </GlanceBox>
    );
}


/* ══════════════════════════════════════════════════════════════════
   3 ▸ Clinical Protocol
═══════════════════════════════════════════════════════════════════ */

function ClinicalProtocolLeft({ plan, textRevs }) {
    return (
        <div className="space-y-1">
            <ProtoRow label="Attachments — location & stage" text={plan.proposed_attachments}
                changed={!!textRevs.attachments_revision} />
            <ProtoRow label="IPR by region & stage" text={plan.proposed_ipr}
                changed={!!textRevs.ipr_revision} />
            <ProtoRow label="Elastics protocol" text={plan.proposed_elastics}
                changed={!!textRevs.elastics_revision} />
            <ProtoRow label="Planner notes & rationale" text={plan.planner_notes}
                changed={!!textRevs.rationale_revision} />
            <ProtoRow label="Overcorrection"
                badge={plan.has_overcorrection
                    ? <Badge tone="teal" size="xs">On</Badge>
                    : <Badge tone="gray" size="xs">Off</Badge>}
                text={plan.overcorrection_notes}
                changed={!!textRevs.overcorrection_revision}
            />
        </div>
    );
}

function ClinicalProtocolRight({ textRevs, setTextValue, sent }) {
    return (
        <div className="space-y-1">
            <ProtoRowEdit label="Attachments revision"
                placeholder="e.g. Remove attachment on tooth 23. Reposition on 14 to stage 5…"
                value={textRevs.attachments_revision || ""}
                onChange={(v) => setTextValue("attachments_revision", v)}
                disabled={sent}
                testid="revision-input-attachments" />
            <ProtoRowEdit label="IPR revision"
                placeholder="e.g. Reduce 31–32 IPR from 0.3mm to 0.2mm…"
                value={textRevs.ipr_revision || ""}
                onChange={(v) => setTextValue("ipr_revision", v)}
                disabled={sent}
                testid="revision-input-ipr" />
            <ProtoRowEdit label="Elastics revision"
                placeholder="e.g. Start elastics from stage 8 instead of stage 6…"
                value={textRevs.elastics_revision || ""}
                onChange={(v) => setTextValue("elastics_revision", v)}
                disabled={sent}
                testid="revision-input-elastics" />
            <ProtoRowEdit label="Rationale / notes revision"
                placeholder="e.g. Please reconsider the rotation on UR3 — current plan seems aggressive…"
                value={textRevs.rationale_revision || ""}
                onChange={(v) => setTextValue("rationale_revision", v)}
                disabled={sent}
                testid="revision-input-rationale" />
            <ProtoRowEdit label="Overcorrection revision"
                placeholder="e.g. Remove overcorrection steps — not appropriate for this patient…"
                value={textRevs.overcorrection_revision || ""}
                onChange={(v) => setTextValue("overcorrection_revision", v)}
                disabled={sent}
                testid="revision-input-overcorrection" />
        </div>
    );
}


/* ══════════════════════════════════════════════════════════════════
   4 ▸ Per-Tooth Plan — Authored Movements
═══════════════════════════════════════════════════════════════════ */

/* ══════════════════════════════════════════════════════════════════
   4–7 ▸ Per-Tooth Plan (one section per category)
   Both columns mirror each other: LEFT = planner's compact chart +
   authored summary list (read-only). RIGHT = same mirrored compact
   chart + a single revision text field below it.
═══════════════════════════════════════════════════════════════════ */

function PerToothRevisionRight({ plan, category, label, placeholder, value, onChange, disabled, testid }) {
    return (
        <div className="space-y-3">
            <PerToothChartOnly plan={plan} category={category} compact />
            <ProtoRowEdit
                label={label}
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                disabled={disabled}
                testid={testid}
            />
        </div>
    );
}


/* ══════════════════════════════════════════════════════════════════
   5 ▸ Aligners & Wear Schedule
═══════════════════════════════════════════════════════════════════ */

function WearScheduleLeft({ plan, struct, textRevs }) {
    const cadence = plan.change_interval_days || plan.days_per_aligner;
    const upper = plan.aligner_count_upper || 0;
    const lower = plan.aligner_count_lower || 0;
    const total = upper + lower;
    const weeks = plan.estimated_duration_weeks;
    const wearOverrides = Array.isArray(plan.wear_overrides) ? plan.wear_overrides : [];
    return (
        <div className="space-y-3">
            <GlanceBox>
                <GlanceRow
                    k="Standard wear"
                    v={cadence ? `${cadence} days per stage` : null}
                    changed={!!struct.wear_days}
                />
                <GlanceRow k="Upper aligners" v={upper || null} />
                <GlanceRow k="Lower aligners" v={lower || null} />
                {total > 0 && <GlanceRow k="Total stages" v={total} />}
                {weeks > 0 && (
                    <GlanceRow
                        k="Total treatment duration"
                        v={`${weeks * 7} days (${weeks} weeks)`}
                    />
                )}
            </GlanceBox>
            {wearOverrides.length > 0 && (
                <div
                    className={`rounded-md border border-border bg-card/60 p-3 ${textRevs.wear_schedule_notes ? "border-amber-300 bg-amber-50/40" : ""}`}
                >
                    <FieldLabel>Stage-specific overrides</FieldLabel>
                    <div className="space-y-1 text-[12.5px]">
                        {wearOverrides.map((ov, i) => (
                            <div key={`${ov.stage}-${i}`}>
                                <span className="font-semibold">Stage {ov.stage}:</span> {ov.days} days
                                {ov.note ? <span className="text-muted-foreground"> — {ov.note}</span> : null}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function WearScheduleRight({ struct, setStructValue, textRevs, setTextValue, sent }) {
    return (
        <div className="space-y-3">
            <GlanceBox>
                <NumInputRow
                    label="Wear schedule" unit="days" placeholder="e.g. 10"
                    value={struct.wear_days} onChange={(v) => setStructValue("wear_days", v)}
                    min={1} disabled={sent}
                    testid="revision-input-wear-days"
                />
            </GlanceBox>
            <ProtoRowEdit
                label="Wear schedule notes"
                placeholder="e.g. Switch to 5-day wear after stage 8; add 2 weeks of overcorrection at the end…"
                value={textRevs.wear_schedule_notes || ""}
                onChange={(v) => setTextValue("wear_schedule_notes", v)}
                disabled={sent}
                testid="revision-input-wear-schedule-notes"
            />
        </div>
    );
}


/* ══════════════════════════════════════════════════════════════════
   6 ▸ Retention Plan
═══════════════════════════════════════════════════════════════════ */

function RetentionLeft({ plan, struct }) {
    return (
        <GlanceBox>
            <GlanceRow
                k="Retainer type"
                v={plan.retainer_type ? (RETAINER_LABEL[plan.retainer_type] || humanize(plan.retainer_type)) : null}
            />
            <GlanceRow
                k="Follow-up recall"
                v={plan.retention_recall_months ? `${plan.retention_recall_months} months` : (plan.retention_recall_first_months ? `${plan.retention_recall_first_months} months` : null)}
                changed={!!struct.follow_up_recall_months}
            />
            <GlanceRow
                k="Wear protocol"
                v={plan.retention_schedule}
                changed={!!struct.wear_protocol_text}
            />
        </GlanceBox>
    );
}

function RetentionRight({ struct, setStructValue, sent }) {
    /* iter98.99bg — Retainer-type chips removed per user direction
       (2026-02-19): the doctor cannot request a change to the retainer
       type from this surface. Only Follow-up recall and Wear protocol
       are doctor-editable. */
    return (
        <div className="space-y-2">
            <GlanceBox>
                <NumInputRow label="Follow-up recall" unit="months" placeholder="e.g. 3"
                    value={struct.follow_up_recall_months}
                    onChange={(v) => setStructValue("follow_up_recall_months", v)}
                    min={1} disabled={sent}
                    testid="revision-input-recall-months" />
            </GlanceBox>

            <div
                className={`px-3 py-2.5 rounded-md border ${struct.wear_protocol_text ? "bg-amber-50 border-amber-300" : "border-border/40"}`}
                data-testid="revision-row-wear-protocol"
            >
                <FieldLabel>Wear protocol revision</FieldLabel>
                <input
                    type="text"
                    value={struct.wear_protocol_text || ""}
                    onChange={(e) => setStructValue("wear_protocol_text", e.target.value)}
                    placeholder="e.g. Nights only from day 1…"
                    disabled={sent}
                    className={`w-full text-[13px] px-2.5 py-1.5 rounded-md border outline-none transition-colors ${
                        struct.wear_protocol_text
                            ? "border-amber-400 bg-amber-50/40"
                            : "border-border focus:border-primary"
                    } disabled:opacity-55`}
                    data-testid="revision-input-wear-protocol-text"
                />
            </div>
        </div>
    );
}


/* ══════════════════════════════════════════════════════════════════
   Context strip (Overall notes — lives ABOVE the 6 sections so the
   section list matches the Treatment Plan tab exactly).
═══════════════════════════════════════════════════════════════════ */

function ContextStrip({ overallNotes, onOverallChange, disabled }) {
    return (
        <div
            className="rounded-2xl border border-border bg-card p-4 sm:p-5 mb-5"
            data-testid="revision-context-strip"
        >
            <FieldLabel>Overall notes <Optional /></FieldLabel>
            <RevTextarea
                value={overallNotes}
                onChange={onOverallChange}
                placeholder="A one-line gist of what you want changed. e.g. Overall staging feels too aggressive for this patient — please soften the anterior movement in the first 6 stages."
                minHeight={72}
                disabled={disabled}
                testid="revision-input-overall-notes"
            />
            <p className="text-[11px] text-muted-foreground/70 italic mt-1">
                This becomes the planner&apos;s first read — keep it brief and use the per-section fields below for the detailed changes.
            </p>
        </div>
    );
}


/* ──────────────────────────────────────────────────────────────── */
/* Sub-components (small, local)                                    */
/* ──────────────────────────────────────────────────────────────── */

function Badge({ tone = "gray", size = "sm", children, ...rest }) {
    const toneCls = {
        amber: "bg-amber-100 text-amber-800 border-amber-300",
        teal:  "bg-emerald-50 text-emerald-900 border-primary/40",
        gray:  "bg-secondary text-muted-foreground border-border",
    }[tone];
    const sizeCls = size === "xs" ? "text-[10px] px-1.5 py-0.5" : "text-[11px] px-2 py-0.5";
    return (
        <span
            className={`inline-flex items-center gap-1 font-medium rounded-full border ${toneCls} ${sizeCls}`}
            {...rest}
        >
            {children}
        </span>
    );
}

function ColumnHeader({ icon, title, sub, rightBadge }) {
    return (
        <div className="flex items-center justify-between bg-card border border-border first:rounded-l-lg last:rounded-r-lg first:border-r-0 px-4 py-2.5 md:[&:first-child]:rounded-r-none md:[&:last-child]:rounded-l-none">
            <div className="flex flex-col gap-0.5">
                <div className="text-[13px] font-semibold flex items-center gap-1.5">
                    {icon}{title}
                </div>
                <div className="text-[11px] text-muted-foreground/70">{sub}</div>
            </div>
            {rightBadge}
        </div>
    );
}

function FieldLabel({ children }) {
    return <div className="text-xs font-medium text-muted-foreground mb-1.5">{children}</div>;
}

function Optional() {
    return <span className="text-muted-foreground/70 font-normal text-[11px]">(optional)</span>;
}

function Dash() { return <span className="text-muted-foreground">—</span>; }

function GlanceBox({ children }) {
    return <div className="border border-border rounded-md overflow-hidden bg-card">{children}</div>;
}

function GlanceRow({ k, v, changed }) {
    const safeK = String(k).toLowerCase().replace(/[^a-z0-9]+/g, "-");
    return (
        <div
            className={`flex justify-between items-center text-[13px] px-3 py-2 border-b border-border/40 last:border-b-0 transition-colors ${
                changed ? "bg-amber-50 border-l-[3px] border-l-amber-400" : ""
            }`}
            data-testid={`planner-row-${safeK}`}
        >
            <span className={`text-xs ${changed ? "text-amber-900/70" : "text-muted-foreground"}`}>{k}</span>
            <span className={`font-medium ${changed ? "text-amber-900" : ""}`}>
                {v === null || v === undefined || v === "" ? <Dash /> : v}
            </span>
        </div>
    );
}

function NumInputRow({ label, unit, placeholder, value, onChange, min, max, step, disabled, testid }) {
    const filled = value !== undefined && value !== null && value !== "";
    return (
        <div
            className={`flex justify-between items-center text-[13px] px-3 py-1.5 border-b border-border/40 last:border-b-0 gap-2 ${
                filled ? "bg-amber-50" : ""
            }`}
        >
            <span className="text-xs text-muted-foreground min-w-[120px]">{label}</span>
            <div
                className={`flex items-center border rounded-md overflow-hidden bg-card max-w-[160px] ${
                    filled ? "border-amber-400" : "border-border"
                }`}
            >
                <input
                    type="number"
                    placeholder={placeholder}
                    value={value ?? ""}
                    onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
                    min={min} max={max} step={step}
                    disabled={disabled}
                    className="border-none outline-none px-2.5 py-1 text-[13px] bg-transparent w-full disabled:opacity-55"
                    data-testid={testid}
                />
                <span className="text-[11px] text-muted-foreground/70 px-2 border-l border-border whitespace-nowrap">
                    {unit}
                </span>
            </div>
        </div>
    );
}

function ProtoRow({ label, text, badge, changed }) {
    return (
        <div
            className={`mb-3 last:mb-0 p-2.5 rounded-md transition-colors ${
                changed ? "bg-amber-50" : ""
            }`}
        >
            <FieldLabel>{label}</FieldLabel>
            {badge && <div className="mb-1.5">{badge}</div>}
            {text ? (
                <div className="text-[13px] leading-relaxed whitespace-pre-wrap">{text}</div>
            ) : (
                <span className="text-xs text-muted-foreground/70 italic">—</span>
            )}
        </div>
    );
}

function ProtoRowEdit({ label, placeholder, value, onChange, disabled, testid }) {
    const filled = !!(value || "").trim();
    return (
        <div className={`mb-3 last:mb-0 p-2.5 rounded-md transition-colors ${filled ? "bg-amber-50" : ""}`}>
            <FieldLabel>{label}</FieldLabel>
            <RevTextarea value={value} onChange={onChange} placeholder={placeholder} disabled={disabled} testid={testid} />
        </div>
    );
}

function RevTextarea({ value, onChange, placeholder, disabled, minHeight = 72, testid }) {
    const filled = !!(value || "").trim();
    return (
        <Textarea
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            data-testid={testid}
            className={`w-full text-[13px] resize-y leading-relaxed transition-colors ${
                filled ? "border-amber-400 bg-amber-50/40" : "border-border focus-visible:border-primary"
            }`}
            style={{ minHeight }}
        />
    );
}

function NoteBox({ tone, children, className = "" }) {
    const toneCls = tone === "teal"
        ? "bg-emerald-50 text-emerald-900"
        : "bg-amber-50 text-amber-900";
    return (
        <div className={`flex items-start gap-2 px-3 py-2.5 rounded-md text-xs leading-snug ${toneCls} ${className}`}>
            {children}
        </div>
    );
}


/* iter98.87 — "Review your changes" diff preview dialog. Unchanged
   structurally from the previous iter; just retoned to the brand
   primary instead of the old hard-coded green. */
function ReviewChangesDialog({ open, onClose, rows, submitting, onConfirm }) {
    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent
                className="max-w-lg p-0 gap-0 overflow-hidden"
                data-testid="revision-review-dialog"
            >
                <DialogHeader className="px-5 py-4 bg-amber-50 border-b border-amber-200">
                    <DialogTitle className="text-[15px] font-bold text-amber-900 flex items-center gap-2">
                        <Info size={15} weight="duotone" /> Review your changes
                    </DialogTitle>
                    <DialogDescription className="text-[12px] text-amber-800/80">
                        About to send <b>{rows.length}</b> change{rows.length === 1 ? "" : "s"} to the planning team. Make sure the values are right.
                    </DialogDescription>
                </DialogHeader>

                <div className="max-h-[55vh] overflow-y-auto px-5 py-4 space-y-2" data-testid="revision-review-rows">
                    {rows.length === 0 ? (
                        <p className="text-sm text-muted-foreground italic">No changes yet.</p>
                    ) : (
                        rows.map((r) => (
                            <div
                                key={r.key}
                                className="flex items-start gap-2.5 text-[13px] leading-snug py-1.5 border-b border-border last:border-b-0"
                                data-testid={`review-row-${r.key}`}
                            >
                                <Circle size={6} weight="fill" className="text-amber-500 mt-2 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-foreground">{r.label}</div>
                                    {r.kind === "struct" ? (
                                        <div className="text-muted-foreground tabular-nums">
                                            <span className="line-through opacity-60">{r.from}</span>
                                            <ArrowRight size={11} className="inline mx-1.5 align-middle text-amber-600" weight="bold" />
                                            <span className="font-semibold text-foreground">{r.to}</span>
                                        </div>
                                    ) : (
                                        <div className="text-[12px] text-muted-foreground italic">
                                            “{r.preview}”
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border bg-secondary/30">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        disabled={submitting}
                        data-testid="revision-review-back-btn"
                    >
                        Back to edit
                    </Button>
                    <Button
                        onClick={onConfirm}
                        disabled={submitting || rows.length === 0}
                        className="bg-primary hover:bg-primary/90 text-white"
                        data-testid="revision-review-confirm-btn"
                    >
                        {submitting ? "Sending…" : "Send Revision Request"} <ArrowRight size={13} weight="bold" className="ml-1.5" />
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
